import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import { useTracker } from '../context/TrackerContext';
import { adminSetSlot, mentorSetSlot, getAdminMentors, getMentorStudents, getTimetableSlots, getJuniorMentorOverview, getMentorSlotConfig, setMentorSlotConfig, sendClassReminders, getStudentQuickStats } from '../lib/api';
import type { MentorInfo, StudentBasic, TimetableSlot, JuniorMentor, StudentQuickStats } from '../lib/api';
import { ReassignMentorModal, StudentDetailModal } from '../components/StudentModals';

// ==================== CONSTANTS ==============================

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export type DayKey = typeof DAYS[number];

function todayDayKey(): DayKey | null {
  const d = new Date().getDay(); // 0=Sun,1=Mon…6=Sat
  const map: Record<number, DayKey> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' };
  return map[d] ?? null;
}

const WEEKDAYS: DayKey[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

interface TimeSlot { start: string; end: string; label: string; isLunch: boolean }

export interface CustomSlot { start: string; end: string; isLunch?: boolean }
export interface SlotConfig {
  startTime: string; endTime: string; durationMin: number;
  customSlots?: CustomSlot[];
}

const DEFAULT_CONFIG: SlotConfig = { startTime: '09:00', endTime: '21:00', durationMin: 45 };
const CONFIG_KEY_BASE = 'vt_timetable_config';
function configKey(mentorId?: string) {
  return mentorId ? `${CONFIG_KEY_BASE}_${mentorId}` : CONFIG_KEY_BASE;
}

export function loadSlotConfig(mentorId?: string): SlotConfig {
  try {
    const raw = localStorage.getItem(configKey(mentorId));
    if (raw) return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
  } catch {}
  return DEFAULT_CONFIG;
}

export function saveSlotConfig(cfg: SlotConfig, mentorId?: string) {
  localStorage.setItem(configKey(mentorId), JSON.stringify(cfg));
}

// A mentor's slot settings as saved on the server. The app saves only
// customSlots, so the start/end/duration fall back to the defaults.
function dbSlotConfig(raw: object | null | undefined): SlotConfig | null {
  return raw ? { ...DEFAULT_CONFIG, ...(raw as Partial<SlotConfig>) } : null;
}

// Parse "9:30 AM" / "9:30 PM" → "09:30" (24-hr for storage). Returns null if invalid.
function parse12(t: string): string | null {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (h < 1 || h > 12 || min > 59) return null;
  if (ap === 'AM') h = h === 12 ? 0 : h;
  else              h = h === 12 ? 12 : h + 12;
  return `${h.toString().padStart(2, '0')}:${m[2]}`;
}

// Time input that displays in 12-hour format (e.g. "9:30 AM").
// Internally stores and emits 24-hour "HH:MM" strings for the DB.
const TimeInput: React.FC<{
  value: string;
  onCommit: (v: string) => void;
  className?: string;
  style?: React.CSSProperties;
}> = ({ value, onCommit, className, style }) => {
  const [local, setLocal] = React.useState(() => fmt12(value));
  React.useEffect(() => { setLocal(fmt12(value)); }, [value]);
  return (
    <input
      type="text"
      value={local}
      placeholder="9:30 AM"
      maxLength={8}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        const parsed = parse12(local);
        if (parsed) {
          setLocal(fmt12(parsed));
          onCommit(parsed);
        } else {
          setLocal(fmt12(value)); // revert to last valid
        }
      }}
      className={className}
      style={style}
    />
  );
};

function fmt12(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${m.toString().padStart(2, '0')} ${ap}`;
}

export function generateSlots(cfg: SlotConfig = DEFAULT_CONFIG): TimeSlot[] {
  // If custom slots are defined, use them directly
  if (cfg.customSlots && cfg.customSlots.length > 0) {
    return cfg.customSlots
      .slice()
      .sort((a, b) => a.start.localeCompare(b.start))
      .map(s => ({
        start: s.start,
        end: s.end,
        isLunch: !!s.isLunch,
        label: s.isLunch
          ? `${fmt12(s.start)} – ${fmt12(s.end)} (Lunch)`
          : `${fmt12(s.start)} – ${fmt12(s.end)}`,
      }));
  }
  // Otherwise auto-generate uniform slots
  const slots: TimeSlot[] = [];
  const pad = (n: number) => n.toString().padStart(2, '0');
  const str = (hh: number, mm: number) => `${pad(hh)}:${pad(mm)}`;
  const [startH, startM] = cfg.startTime.split(':').map(Number);
  const [endH, endM]     = cfg.endTime.split(':').map(Number);
  const endTotal = endH * 60 + endM;
  let h = startH, m = startM;
  while (true) {
    const curTotal = h * 60 + m;
    if (curTotal >= endTotal) break;
    if (h === 12 && m === 45) {
      slots.push({ start: '12:45', end: '13:00', label: '12:45 PM – 1:00 PM', isLunch: true });
      h = 13; m = 0; continue;
    }
    const nextTotal = curTotal + cfg.durationMin;
    const endHH = Math.floor(nextTotal / 60);
    const endMM = nextTotal % 60;
    if (nextTotal > endTotal) break;
    slots.push({ start: str(h, m), end: str(endHH, endMM), label: `${fmt12(str(h,m))} – ${fmt12(str(endHH,endMM))}`, isLunch: false });
    h = endHH; m = endMM;
  }
  return slots;
}

// Grid: day → slotStart → array of students
type CellStudents = Array<{ studentId: string; studentName: string }>;
type Grid = Record<string, Record<string, CellStudents>>;

function buildGrid(slots: TimetableSlot[]): Grid {
  const grid: Grid = {};
  DAYS.forEach(d => { grid[d] = {}; });
  slots.forEach(s => {
    if (!grid[s.day]) grid[s.day] = {};
    if (!grid[s.day][s.slotStart]) grid[s.day][s.slotStart] = [];
    if (!grid[s.day][s.slotStart].find(x => x.studentId === s.studentId)) {
      grid[s.day][s.slotStart].push({ studentId: s.studentId, studentName: s.studentName });
    }
  });
  return grid;
}

// ==================== STUDENT HOVER CARD ====================

const CARD_STYLES = `
@keyframes hoverCardIn {
  0%   { opacity:0; transform:scale(0.82) translateY(6px); }
  60%  { opacity:1; transform:scale(1.03) translateY(-1px); }
  100% { opacity:1; transform:scale(1)    translateY(0);    }
}
@keyframes ringFill {
  from { stroke-dashoffset: var(--dash-total); }
  to   { stroke-dashoffset: var(--dash-empty); }
}
@keyframes barSlide {
  from { width: 0; }
  to   { width: var(--bar-w); }
}
@keyframes fadeUp {
  from { opacity:0; transform:translateY(8px); }
  to   { opacity:1; transform:translateY(0);   }
}
.hoverCard { animation: hoverCardIn .28s cubic-bezier(.34,1.56,.64,1) both; }
.hoverCard .ring-track { animation: ringFill .7s .15s cubic-bezier(.4,0,.2,1) both; }
.hoverCard .bar-fill   { animation: barSlide .6s .2s cubic-bezier(.4,0,.2,1) both; }
.hoverCard .stat-row   { animation: fadeUp .3s both; }
.hoverCard .stat-row:nth-child(1) { animation-delay:.08s; }
.hoverCard .stat-row:nth-child(2) { animation-delay:.16s; }
.hoverCard .stat-row:nth-child(3) { animation-delay:.24s; }
.hoverCard .stat-row:nth-child(4) { animation-delay:.32s; }
`;

interface HoverPos { x: number; y: number }

const statsCache = new Map<string, StudentQuickStats>();

const StudentHoverCard: React.FC<{
  studentId: string;
  studentName: string;
  pos: HoverPos;
  onViewDetails?: () => void;
  onReassign?: () => void;
  onDeactivate?: () => void;
  onCardEnter?: () => void;
  onCardLeave?: () => void;
}> = ({ studentId, studentName, pos, onViewDetails, onReassign, onDeactivate, onCardEnter, onCardLeave }) => {
  const [stats, setStats] = useState<StudentQuickStats | null>(statsCache.get(studentId) ?? null);
  const [loading, setLoading] = useState(!statsCache.has(studentId));

  useEffect(() => {
    if (statsCache.has(studentId)) { setStats(statsCache.get(studentId)!); setLoading(false); return; }
    setLoading(true);
    getStudentQuickStats(studentId)
      .then(r => { statsCache.set(studentId, r.stats); setStats(r.stats); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  // Smart positioning — keep card on screen
  const vpW = window.innerWidth, vpH = window.innerHeight;
  const cardW = 270, cardH = 310;
  const left = pos.x + cardW + 12 > vpW ? pos.x - cardW - 8 : pos.x + 12;
  const top  = pos.y + cardH + 8 > vpH ? pos.y - cardH + 10 : pos.y - 10;

  const initials = studentName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const RING_R = 28, RING_CIRC = 2 * Math.PI * RING_R;
  const attOffset = stats ? RING_CIRC * (1 - stats.attPct / 100) : RING_CIRC;

  return (
    <>
      <style>{CARD_STYLES}</style>
      <div
        className="hoverCard"
        onMouseEnter={onCardEnter}
        onMouseLeave={onCardLeave}
        style={{
          position: 'fixed', left, top, zIndex: 9999, width: cardW,
          background: 'linear-gradient(145deg,#1a1330 0%,#2b2240 60%,#3d2f5e 100%)',
          borderRadius: 18, boxShadow: '0 20px 60px rgba(0,0,0,.55), 0 0 0 1px rgba(240,180,41,.18)',
          padding: '16px', pointerEvents: 'all', userSelect: 'none',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontWeight: 900,
            fontSize: 14, color: '#fff',
            background: 'linear-gradient(135deg,#7c3aed,#a855f7)',
            boxShadow: '0 4px 12px rgba(124,58,237,.5)',
          }}>{initials}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 13, lineHeight: 1.2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{studentName}</div>
            <div style={{ color: '#a78bfa', fontSize: 10, fontWeight: 600, marginTop: 2 }}>{studentId}</div>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[80, 60, 70].map((w, i) => (
              <div key={i} style={{ height: 10, borderRadius: 6, background: 'rgba(255,255,255,.08)',
                width: `${w}%`, animation: 'pulse 1.4s ease-in-out infinite' }} />
            ))}
          </div>
        ) : stats ? (
          <>
            {/* Attendance ring + number */}
            <div className="stat-row" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
              <svg width={68} height={68} style={{ flexShrink: 0 }}>
                {/* bg track */}
                <circle cx={34} cy={34} r={RING_R} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth={6} />
                {/* fill */}
                <circle
                  className="ring-track"
                  cx={34} cy={34} r={RING_R} fill="none"
                  stroke={stats.attPct >= 75 ? '#4ade80' : stats.attPct >= 50 ? '#fbbf24' : '#f87171'}
                  strokeWidth={6} strokeLinecap="round"
                  strokeDasharray={RING_CIRC}
                  style={{
                    '--dash-total': RING_CIRC,
                    '--dash-empty': attOffset,
                    strokeDashoffset: attOffset,
                    transform: 'rotate(-90deg)', transformOrigin: '34px 34px',
                  } as React.CSSProperties}
                />
                <text x={34} y={34} textAnchor="middle" dominantBaseline="central"
                  fill="#fff" fontSize={12} fontWeight={800}>{stats.attPct}%</text>
              </svg>
              <div>
                <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Attendance</div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, lineHeight: 1 }}>{stats.attended}<span style={{ color: 'rgba(255,255,255,.4)', fontWeight: 500, fontSize: 12 }}>/{stats.total}</span></div>
                <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 10, marginTop: 3 }}>classes attended</div>
              </div>
            </div>

            {/* Task stats */}
            {stats.taskTotal > 0 && (
              <div className="stat-row" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Tasks</span>
                  <span style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700 }}>{stats.taskDone}/{stats.taskTotal}</span>
                </div>
                {/* Completed bar */}
                <div style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ color: '#4ade80', fontSize: 10, fontWeight: 600 }}>✓ Completed</span>
                    <span style={{ color: '#4ade80', fontSize: 10, fontWeight: 700 }}>{stats.taskDonePct}%</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                    <div className="bar-fill" style={{
                      height: '100%', borderRadius: 3, background: 'linear-gradient(90deg,#22c55e,#4ade80)',
                      '--bar-w': `${stats.taskDonePct}%`, width: `${stats.taskDonePct}%`,
                    } as React.CSSProperties} />
                  </div>
                </div>
                {/* Missed bar */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ color: '#f87171', fontSize: 10, fontWeight: 600 }}>✗ Missed</span>
                    <span style={{ color: '#f87171', fontSize: 10, fontWeight: 700 }}>{stats.taskTotal ? Math.round(stats.taskMiss / stats.taskTotal * 100) : 0}%</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                    <div className="bar-fill" style={{
                      height: '100%', borderRadius: 3, background: 'linear-gradient(90deg,#ef4444,#f87171)',
                      '--bar-w': `${stats.taskTotal ? Math.round(stats.taskMiss / stats.taskTotal * 100) : 0}%`,
                      width: `${stats.taskTotal ? Math.round(stats.taskMiss / stats.taskTotal * 100) : 0}%`,
                      animationDelay: '.28s',
                    } as React.CSSProperties} />
                  </div>
                </div>
              </div>
            )}

            {/* Last class */}
            {stats.recent && (
              <div className="stat-row" style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,.05)' }}>
                <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Last Class</div>
                <div style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{stats.recent.topic || '—'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ color: 'rgba(255,255,255,.35)', fontSize: 10 }}>{stats.recent.date}</span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    background: stats.recent.attendance === 'Present' ? 'rgba(74,222,128,.15)' : 'rgba(248,113,113,.15)',
                    color: stats.recent.attendance === 'Present' ? '#4ade80' : '#f87171',
                  }}>{stats.recent.attendance}</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>No class data yet</div>
        )}

        {/* Action buttons */}
        {(onViewDetails || onReassign || onDeactivate) && (
          <div className="stat-row" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            {onViewDetails && (
              <button onClick={onViewDetails} style={{
                width: '100%', padding: '8px 0', borderRadius: 9,
                border: '1.5px solid rgba(240,180,41,.35)',
                background: 'rgba(240,180,41,.1)', color: '#F0B429', fontSize: 11, fontWeight: 800,
                cursor: 'pointer', transition: 'all .15s', letterSpacing: .3,
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(240,180,41,.2)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(240,180,41,.1)'; }}
              >⊞ View Full Details</button>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
            {onReassign && (
              <button onClick={onReassign} style={{
                flex: 1, padding: '7px 0', borderRadius: 9, border: '1.5px solid rgba(167,139,250,.4)',
                background: 'rgba(167,139,250,.1)', color: '#c4b5fd', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', transition: 'all .15s',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(167,139,250,.2)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(167,139,250,.1)'; }}
              >↔ Reassign</button>
            )}
            {onDeactivate && (
              <button onClick={onDeactivate} style={{
                flex: 1, padding: '7px 0', borderRadius: 9, border: '1.5px solid rgba(248,113,113,.35)',
                background: 'rgba(248,113,113,.08)', color: '#fca5a5', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', transition: 'all .15s',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(248,113,113,.18)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(248,113,113,.08)'; }}
              >◑ Remove Slot</button>
            )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Hook to manage hover card state with delay
function useStudentHover(delayMs = 250) {
  const [hovered, setHovered] = useState<{ studentId: string; studentName: string; pos: HoverPos } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };

  const onEnter = useCallback((studentId: string, studentName: string, e: React.MouseEvent) => {
    clearTimer();
    const pos = { x: e.clientX, y: e.clientY };
    timerRef.current = setTimeout(() => setHovered({ studentId, studentName, pos }), delayMs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayMs]);

  const onMove = useCallback((e: React.MouseEvent) => {
    setHovered(prev => prev ? { ...prev, pos: { x: e.clientX, y: e.clientY } } : null);
  }, []);

  const onLeave = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setHovered(null), 120);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Card keeps itself alive while hovered
  const onCardEnter = useCallback(() => { clearTimer(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const onCardLeave = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setHovered(null), 120);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useCallback(() => { clearTimer(); setHovered(null); }, []);

  useEffect(() => () => clearTimer(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return { hovered, onEnter, onMove, onLeave, onCardEnter, onCardLeave, dismiss };
}

//==================== MENTOR VIEW ============================

const MentorTimetableView: React.FC<{ mentorId: string; editable?: boolean }> = ({ mentorId, editable = false }) => {
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [mentorStudents, setMentorStudents] = useState<StudentBasic[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const hover = useStudentHover();
  const [TIME_SLOTS, setTimeSlots] = useState(() => generateSlots(loadSlotConfig(mentorId)));
  useEffect(() => {
    getMentorSlotConfig(mentorId)
      .then(res => {
        const cfg: SlotConfig = dbSlotConfig(res.slotConfig) || loadSlotConfig(mentorId);
        saveSlotConfig(cfg, mentorId);
        setTimeSlots(generateSlots(cfg));
      })
      .catch(() => {});
  }, [mentorId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        getTimetableSlots(mentorId),
        editable ? getMentorStudents(mentorId) : Promise.resolve({ ok: true as const, students: [] }),
      ]);
      setSlots(tRes.slots ?? []);
      setMentorStudents(sRes.students ?? []);
    } catch (err: any) {
      toast.error('Failed to load timetable: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [mentorId, editable]);

  useEffect(() => { load(); }, [load]);

  const grid = useMemo(() => buildGrid(slots), [slots]);

  const globallyAssignedIds = useMemo(() => new Set(
    Object.values(grid).flatMap(dayMap => Object.values(dayMap).flatMap(ss => ss.map(s => s.studentId)))
  ), [grid]);

  const unassignedStudents = useMemo(
    () => mentorStudents.filter(s => !globallyAssignedIds.has(s.studentId)),
    [mentorStudents, globallyAssignedIds]
  );

  const availableStudents = useMemo(
    () => mentorStudents.filter(s => !globallyAssignedIds.has(s.studentId)),
    [mentorStudents, globallyAssignedIds]
  );

  const handleAssignWeekdays = async (slotStart: string, studentId: string) => {
    if (!studentId) { setEditingRow(null); return; }
    const student = mentorStudents.find(s => s.studentId === studentId);
    if (!student) return;
    setSaving(true);
    try {
      await mentorSetSlot(mentorId, student.studentId, student.name, [...WEEKDAYS], slotStart, 'add');
      setSlots(prev => {
        const next = [...prev];
        WEEKDAYS.forEach(day => {
          if (!next.some(s => s.studentId === student.studentId && s.day === day && s.slotStart === slotStart)) {
            next.push({ mentorId, studentId: student.studentId, studentName: student.name, day, slotStart });
          }
        });
        return next;
      });
      setEditingRow(null);
      toast.success(`${student.name} added Mon–Fri at ${slotStart}`);
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleRemoveStudent = async (slotStart: string, studentId: string, studentName: string) => {
    const daysPresent = DAYS.filter(d => grid[d]?.[slotStart]?.some(s => s.studentId === studentId));
    if (!window.confirm(`Remove ${studentName} from all days at this time slot?`)) return;
    setSaving(true);
    try {
      await mentorSetSlot(mentorId, studentId, studentName, [...daysPresent], slotStart, 'remove');
      setSlots(prev => prev.filter(s => !(s.studentId === studentId && s.slotStart === slotStart)));
      toast.success(`${studentName} removed from this slot`);
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const todayKey = todayDayKey();

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-3">
      <div className="w-7 h-7 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm">Loading timetable…</span>
    </div>
  );

  if (!editable && slots.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-10 text-center">
        <div className="text-4xl mb-3">📅</div>
        <p className="text-amber-800 font-bold text-lg">No sessions assigned yet</p>
        <p className="text-amber-700 text-sm mt-1">Your admin will assign your student sessions to the timetable.</p>
      </div>
    );
  }

  const extraCols = editable ? 1 : 0;

  return (
    <div className="space-y-3">
      {saving && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-700 font-medium w-fit">
          <div className="w-3 h-3 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
          Saving changes…
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: 'linear-gradient(135deg,#1e1730,#2b2240)' }}>
                <th className="px-4 py-3.5 text-left text-xs font-bold text-white/70 uppercase tracking-wide w-44 sticky left-0" style={{ background: '#1e1730' }}>
                  Time Slot
                </th>
                {DAYS.map(day => {
                  const isToday = day === todayKey;
                  return (
                    <th key={day} className="px-3 py-3.5 text-center text-xs font-bold min-w-[130px] transition-colors"
                      style={{ color: isToday ? '#F0B429' : 'rgba(255,255,255,0.7)' }}>
                      <span className={isToday ? 'inline-flex flex-col items-center gap-0.5' : ''}>
                        {day}
                        {isToday && <span className="text-[9px] font-black bg-yellow-400 text-yellow-900 px-1.5 py-0 rounded-full leading-4">TODAY</span>}
                      </span>
                    </th>
                  );
                })}
                {editable && (
                  <th className="px-3 py-3.5 text-center text-xs font-bold w-28" style={{ color: '#F0B429' }}>+ Mon–Fri</th>
                )}
              </tr>
            </thead>
            <tbody>
              {TIME_SLOTS.map((slot, idx) => {
                if (slot.isLunch) {
                  return (
                    <tr key={idx} style={{ background: '#fef9c3' }}>
                      <td colSpan={DAYS.length + 1 + extraCols} className="px-4 py-2.5 text-center text-xs font-bold text-amber-700 tracking-wide">
                        🍱 Lunch Break · 12:45 – 1:00 PM
                      </td>
                    </tr>
                  );
                }
                const hasAny = DAYS.some(d => (grid[d]?.[slot.start] ?? []).length > 0);
                const isEditingThisRow = editingRow === slot.start;

                return (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-amber-50/30 transition-colors"
                    style={{ background: hasAny ? '#fafafa' : '#fff' }}>
                    <td className="px-4 py-2.5 whitespace-nowrap sticky left-0 bg-inherit"
                      style={{ borderRight: '2px solid #f3f4f6' }}>
                      <span className="text-xs font-bold" style={{ color: '#2b2240' }}>{fmt12(slot.start)}</span>
                      <span className="text-[10px] text-gray-400 ml-1">–{fmt12(slot.end)}</span>
                    </td>
                    {DAYS.map(day => {
                      const isToday = day === todayKey;
                      const students = grid[day]?.[slot.start] ?? [];
                      return (
                        <td key={day} className="px-2 py-2 text-center align-middle transition-colors"
                          style={isToday && students.length > 0 ? { background: '#fefce8' } : undefined}>
                          {students.length > 0 ? (
                            <div className="flex flex-col gap-1.5 items-center">
                              {students.map(s => (
                                <div key={s.studentId} className="flex items-center gap-1">
                                  <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm cursor-pointer transition-transform hover:scale-105"
                                    style={{ background: isToday ? '#F0B429' : '#ede9fe', color: isToday ? '#451a03' : '#5b21b6' }}
                                    onMouseEnter={e => hover.onEnter(s.studentId, s.studentName, e)}
                                    onMouseMove={hover.onMove}
                                    onMouseLeave={hover.onLeave}
                                  >
                                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black text-white flex-shrink-0"
                                      style={{ background: isToday ? '#92400e' : '#7c3aed' }}>
                                      {s.studentName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                    </span>
                                    {s.studentName}
                                  </span>
                                  {editable && (
                                    <button onClick={() => handleRemoveStudent(slot.start, s.studentId, s.studentName)}
                                      disabled={saving}
                                      className="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors rounded-full hover:bg-red-50 text-sm disabled:opacity-40"
                                      title={`Remove ${s.studentName}`}>×</button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-200 text-xs select-none">·</span>
                          )}
                        </td>
                      );
                    })}
                    {editable && (
                      <td className="px-2 py-1.5 text-center">
                        {isEditingThisRow ? (
                          <select autoFocus defaultValue=""
                            onChange={e => handleAssignWeekdays(slot.start, e.target.value)}
                            onBlur={() => setEditingRow(null)}
                            className="w-full text-xs border-2 border-yellow-400 rounded-lg px-1.5 py-1 outline-none bg-white"
                            disabled={saving}>
                            <option value="">— pick student —</option>
                            {availableStudents.map(s => <option key={s.studentId} value={s.studentId}>{s.name}</option>)}
                            {availableStudents.length === 0 && <option disabled>All assigned</option>}
                          </select>
                        ) : (
                          <button onClick={() => setEditingRow(slot.start)}
                            disabled={saving || availableStudents.length === 0}
                            className="px-3 py-1 rounded-lg text-xs font-semibold border-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{ borderColor: '#ffe500', color: '#312741', background: '#fefce8' }}>
                            + Add
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {editable && mentorStudents.length > 0 && (
        <div className={`rounded-xl border p-4 ${unassignedStudents.length > 0 ? 'border-orange-300 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
          {unassignedStudents.length > 0 ? (
            <>
              <p className="text-sm font-semibold text-orange-700 mb-2">{unassignedStudents.length} student{unassignedStudents.length > 1 ? 's' : ''} not yet assigned to any slot</p>
              <div className="flex flex-wrap gap-2">
                {unassignedStudents.map(s => (
                  <div key={s.studentId} className="flex items-center gap-2 bg-white border border-orange-200 rounded-lg px-3 py-1.5">
                    <span className="text-xs font-mono font-bold text-orange-500">{s.studentId}</span>
                    <span
                      className="text-xs font-semibold text-gray-700 cursor-pointer hover:text-purple-700 hover:underline transition-colors"
                      onMouseEnter={e => hover.onEnter(s.studentId, s.name, e)}
                      onMouseMove={hover.onMove}
                      onMouseLeave={hover.onLeave}
                    >{s.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm font-semibold text-green-700">All students are assigned to a slot</p>
          )}
        </div>
      )}
      {editable && (
        <p className="text-xs text-gray-400">
          Click <strong>+ Add</strong> to assign Mon–Fri. Click <strong>×</strong> to remove a student's slot.
        </p>
      )}

      {/* Student hover card portal */}
      {hover.hovered && (
        <StudentHoverCard
          studentId={hover.hovered.studentId}
          studentName={hover.hovered.studentName}
          pos={hover.hovered.pos}
          onCardEnter={hover.onCardEnter}
          onCardLeave={hover.onCardLeave}
        />
      )}
    </div>
  );
};

// ==================== ADMIN EDIT VIEW ========================

interface MentorCache {
  slots: TimetableSlot[];
  students: StudentBasic[];
  cfg: SlotConfig;
}

const AdminTimetableView: React.FC<{ adminPassword: string }> = ({ adminPassword }) => {
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [mentorStudents, setMentorStudents] = useState<StudentBasic[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const mentorCache = useRef<Map<string, MentorCache>>(new Map());
  const hover = useStudentHover();

  // Modals
  const [detailStudent, setDetailStudent] = useState<{ studentId: string } | null>(null);
  const [reassignTarget, setReassignTarget] = useState<{ studentId: string; studentName: string; mentorId: string; mentorName: string } | null>(null);

  // Timetable config — per-mentor; reloads when the selected mentor changes
  const [slotConfig, setSlotConfig] = useState<SlotConfig>(loadSlotConfig);
  const [showSettings, setShowSettings] = useState(false);
  const [draftConfig, setDraftConfig] = useState<SlotConfig>(loadSlotConfig);

  const adminTimeSlots = generateSlots(slotConfig);

  // Which slot row is showing the student picker
  const [editingRow, setEditingRow] = useState<string | null>(null);
  // Student being moved: { studentId, fromSlot }
  const [movingStudent, setMovingStudent] = useState<{ studentId: string; fromSlot: string } | null>(null);
  // Only show rows that have at least one student assigned
  const [hideEmptyRows, setHideEmptyRows] = useState(false);

  // Build the editable slot list — start from existing customSlots or expand auto-generated ones
  const initDraftSlots = (cfg: SlotConfig): CustomSlot[] => {
    if (cfg.customSlots && cfg.customSlots.length > 0) return cfg.customSlots.map(s => ({ ...s }));
    return generateSlots(cfg).map(s => ({ start: s.start, end: s.end, isLunch: s.isLunch }));
  };

  const [draftSlots, setDraftSlots] = useState<CustomSlot[]>(() => initDraftSlots(loadSlotConfig()));

  const applyConfig = () => {
    const sorted = [...draftSlots].sort((a, b) => a.start.localeCompare(b.start));
    const newCfg: SlotConfig = { ...draftConfig, customSlots: sorted };
    // Save to localStorage (fast cache for this device)
    saveSlotConfig(newCfg, selectedMentorId || undefined);
    setSlotConfig(newCfg);
    setShowSettings(false);
    // Save to DB so any computer (and the app) loads the same config
    if (selectedMentorId) {
      mentorCache.current.delete(selectedMentorId);
      setMentorSlotConfig(adminPassword, selectedMentorId, newCfg)
        .then(() => toast.success('Time slots saved — visible on all devices'))
        .catch(() => toast.warning('Saved locally but could not sync to server'));
    } else {
      toast.success('Time slots updated');
    }
  };

  const openSettings = () => {
    setDraftConfig(slotConfig);
    setDraftSlots(initDraftSlots(slotConfig));
    setShowSettings(s => !s);
  };

  const updateSlot = (i: number, field: 'start' | 'end', val: string) =>
    setDraftSlots(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));

  const removeSlot = (i: number) =>
    setDraftSlots(prev => prev.filter((_, idx) => idx !== i));

  // Add minutes to a HH:MM string, returns HH:MM
  const addMinutes = (hhmm: string, mins: number): string => {
    const [h, m] = hhmm.split(':').map(Number);
    const total = h * 60 + m + mins;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  // Insert a new slot after position i (or at end if i === -1)
  const insertAfter = (i: number) => {
    setDraftSlots(prev => {
      const afterSlot = prev[i];
      const newStart = afterSlot ? addMinutes(afterSlot.end, 0) : '08:00';
      const newEnd   = addMinutes(newStart, 45);
      const next = [...prev];
      next.splice(i + 1, 0, { start: newStart, end: newEnd, isLunch: false });
      return next;
    });
  };

  const addSlot = () => insertAfter(draftSlots.length - 1);

  const toggleLunch = (i: number) =>
    setDraftSlots(prev => prev.map((s, idx) => idx === i ? { ...s, isLunch: !s.isLunch } : s));

  useEffect(() => {
    getAdminMentors(adminPassword)
      .then(res => setMentors(res.mentors.filter((m: any) => m.status === 'Active')))
      .catch(err => toast.error(err.message));
  }, [adminPassword]);

  // Single combined effect — all 3 fetches in one Promise.all, with per-mentor cache
  useEffect(() => {
    if (!selectedMentorId) {
      setSlots([]);
      setMentorStudents([]);
      return;
    }
    setShowSettings(false);

    // Show the last copy at once, but always fetch again — the timetable
    // may have been changed from the app (or another computer) meanwhile
    const cached = mentorCache.current.get(selectedMentorId);
    if (cached) {
      setSlots(cached.slots);
      setMentorStudents(cached.students);
      setSlotConfig(cached.cfg);
      setDraftConfig(cached.cfg);
      setDraftSlots(initDraftSlots(cached.cfg));
    }

    let stale = false;
    setLoading(!cached);
    Promise.all([
      getMentorSlotConfig(selectedMentorId),
      getTimetableSlots(selectedMentorId),
      getMentorStudents(selectedMentorId),
    ])
      .then(([cfgRes, tRes, sRes]) => {
        if (stale) return;
        const dbCfg = dbSlotConfig(cfgRes.slotConfig);
        const cfg = dbCfg || loadSlotConfig(selectedMentorId);
        saveSlotConfig(cfg, selectedMentorId);
        if (!dbCfg) {
          setMentorSlotConfig(adminPassword, selectedMentorId, cfg).catch(() => {});
        }
        setSlotConfig(cfg);
        setDraftConfig(cfg);
        setDraftSlots(initDraftSlots(cfg));
        setSlots(tRes.slots);
        setMentorStudents(sRes.students);
        mentorCache.current.set(selectedMentorId, { slots: tRes.slots, students: sRes.students, cfg });
      })
      .catch(err => { if (!stale) toast.error(err.message); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [selectedMentorId, adminPassword]);

  const grid = useMemo(() => buildGrid(slots), [slots]);

  const globallyAssignedIds = useMemo(() => new Set(
    Object.values(grid).flatMap(dayMap =>
      Object.values(dayMap).flatMap(students => students.map(s => s.studentId))
    )
  ), [grid]);

  const unassignedStudents = useMemo(
    () => mentorStudents.filter(s => !globallyAssignedIds.has(s.studentId)),
    [mentorStudents, globallyAssignedIds]
  );

  const availableStudents = useMemo(
    () => mentorStudents.filter(s => !globallyAssignedIds.has(s.studentId)),
    [mentorStudents, globallyAssignedIds]
  );

  const configuredStarts = useMemo(
    () => new Set(adminTimeSlots.filter(s => !s.isLunch).map(s => s.start)),
    [adminTimeSlots]
  );

  const orphanedStudents = useMemo(() => {
    const orphanedMap = new Map<string, { studentId: string; studentName: string; slotStart: string }>();
    Object.entries(grid).forEach(([, dayMap]) => {
      Object.entries(dayMap).forEach(([slotStart, students]) => {
        if (!configuredStarts.has(slotStart)) {
          students.forEach(s => {
            if (!orphanedMap.has(s.studentId)) {
              orphanedMap.set(s.studentId, { studentId: s.studentId, studentName: s.studentName, slotStart });
            }
          });
        }
      });
    });
    return Array.from(orphanedMap.values());
  }, [grid, configuredStarts]);

  // Invalidate cache for a mentor so next time we fetch fresh
  const invalidateCache = (mentorId: string) => mentorCache.current.delete(mentorId);

  // Assign a student to Mon–Fri in ONE API call (avoids lock contention from parallel calls)
  const handleAssignWeekdays = async (slotStart: string, studentId: string) => {
    if (!studentId) { setEditingRow(null); return; }
    const student = mentorStudents.find(s => s.studentId === studentId);
    if (!student) return;
    setSaving(true);
    try {
      await adminSetSlot(adminPassword, selectedMentorId, student.studentId, student.name, [...WEEKDAYS], slotStart, 'add');
      setSlots(prev => {
        const next = [...prev];
        WEEKDAYS.forEach(day => {
          const exists = next.some(s =>
            s.mentorId === selectedMentorId && s.studentId === student.studentId &&
            s.day === day && s.slotStart === slotStart
          );
          if (!exists) next.push({ mentorId: selectedMentorId, studentId: student.studentId, studentName: student.name, day, slotStart });
        });
        return next;
      });
      invalidateCache(selectedMentorId);
      setEditingRow(null);
      toast.success(`${student.name} added Mon–Fri at ${slotStart}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Remove a student from ALL days in this slot — ONE API call
  const handleRemoveStudent = async (slotStart: string, studentId: string, studentName: string) => {
    const daysPresent = DAYS.filter(d => grid[d]?.[slotStart]?.some(s => s.studentId === studentId));
    if (!window.confirm(`Remove ${studentName} from all days at this time slot?`)) return;
    setSaving(true);
    try {
      await adminSetSlot(adminPassword, selectedMentorId, studentId, studentName, [...daysPresent], slotStart, 'remove');
      setSlots(prev => prev.filter(s => !(s.studentId === studentId && s.slotStart === slotStart)));
      invalidateCache(selectedMentorId);
      toast.success(`${studentName} removed from this slot`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveStudent = async (studentId: string, studentName: string, fromSlot: string, toSlot: string) => {
    if (!toSlot || toSlot === fromSlot) { setMovingStudent(null); return; }
    setSaving(true);
    setMovingStudent(null);
    try {
      const daysPresent = DAYS.filter(d => grid[d]?.[fromSlot]?.some(s => s.studentId === studentId));
      await adminSetSlot(adminPassword, selectedMentorId, studentId, studentName, [...daysPresent], fromSlot, 'remove');
      await adminSetSlot(adminPassword, selectedMentorId, studentId, studentName, [...WEEKDAYS], toSlot, 'add');
      setSlots(prev => {
        const next = prev.filter(s => !(s.studentId === studentId && s.slotStart === fromSlot));
        WEEKDAYS.forEach(day => {
          if (!next.some(s => s.studentId === studentId && s.day === day && s.slotStart === toSlot)) {
            next.push({ mentorId: selectedMentorId, studentId, studentName, day, slotStart: toSlot });
          }
        });
        return next;
      });
      invalidateCache(selectedMentorId);
      toast.success(`${studentName} moved to ${toSlot}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Mentor selector + Settings */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: 'linear-gradient(135deg,#1e1730,#2b2240)' }}>
          <svg className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <span className="text-xs font-bold text-white/80 uppercase tracking-wide">Timetable Editor</span>
          {saving && (
            <div className="ml-auto flex items-center gap-1.5 text-yellow-400 text-xs">
              <div className="w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
              Saving…
            </div>
          )}
        </div>
        <div className="p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="flex-1 flex items-center gap-3 min-w-0">
            {selectedMentorId ? (
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
                {(mentors.find(m => m.mentorId === selectedMentorId)?.mentorName || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
            ) : (
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gray-100 flex-shrink-0">
                <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
            )}
            <select
              value={selectedMentorId}
              onChange={e => { setSelectedMentorId(e.target.value); setEditingRow(null); }}
              className="flex-1 px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors min-w-0"
            >
              <option value="">— choose a mentor to view / edit —</option>
              {mentors.map(m => (
                <option key={m.mentorId} value={m.mentorId}>{m.mentorName} ({m.mentorId})</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => setHideEmptyRows(v => !v)}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all"
              style={hideEmptyRows
                ? { borderColor: '#2b2240', background: '#2b2240', color: '#F0B429' }
                : { borderColor: '#e5e7eb', background: '#fff', color: '#6b7280' }}
              title="Toggle empty row visibility"
            >
              {hideEmptyRows ? '☰ Show all rows' : '⊟ Hide empty'}
            </button>
            <button
              onClick={openSettings}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all"
              style={showSettings
                ? { borderColor: '#2b2240', background: '#2b2240', color: '#F0B429' }
                : { borderColor: '#e5e7eb', background: '#fff', color: '#6b7280' }}
              title="Configure time slots"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              ⚙ Time Settings
            </button>
          </div>
        </div>
      </div>

      {/* Settings panel — per-slot time editor */}
      {showSettings && (
        <div className="bg-white rounded-xl border-2 border-yellow-400 p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-bold" style={{ color: '#2b2240' }}>
                Edit Time Slots ({draftSlots.filter(s => !s.isLunch).length} sessions)
              </h3>
              {selectedMentorId && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Saved separately for this mentor only
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={addSlot}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all"
                style={{ borderColor: '#F0B429', color: '#854d0e', background: '#fef9c3' }}
              >+ Add Slot</button>
              <button
                onClick={() => {
                  const reset: SlotConfig = { ...DEFAULT_CONFIG, customSlots: undefined };
                  saveSlotConfig(reset, selectedMentorId || undefined);
                  setSlotConfig(reset);
                  setDraftSlots(initDraftSlots(reset));
                  setShowSettings(false);
                  // Saved on the server too, or the app keeps the old times
                  if (selectedMentorId) {
                    mentorCache.current.delete(selectedMentorId);
                    setMentorSlotConfig(adminPassword, selectedMentorId, reset)
                      .then(() => toast.success('Reset to default times — visible on all devices'))
                      .catch(() => toast.warning('Reset locally but could not sync to server'));
                  } else {
                    toast.success('Reset to default times');
                  }
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-500 hover:bg-gray-50"
              >Reset Default</button>
            </div>
          </div>

          {/* Column headers */}
          <div className="grid gap-1" style={{ gridTemplateColumns: '1fr 1fr auto auto auto' }}>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">Start (e.g. 9:30 AM)</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">End (e.g. 10:15 AM)</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Lunch?</span>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center">+After</span>
            <span />
          </div>

          {/* Slot rows */}
          <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
            {draftSlots
              .map((s, i) => ({ s, i }))
              .map(({ s, i }) => (
                <div key={i} className="grid gap-2 items-center" style={{ gridTemplateColumns: '1fr 1fr auto auto auto' }}>
                  <TimeInput
                    value={s.start}
                    onCommit={v => updateSlot(i, 'start', v)}
                    className="px-2 py-1.5 border-2 rounded-lg text-sm focus:outline-none focus:border-yellow-400"
                    style={{ borderColor: s.isLunch ? '#fcd34d' : '#e5e7eb' }}
                  />
                  <TimeInput
                    value={s.end}
                    onCommit={v => updateSlot(i, 'end', v)}
                    className="px-2 py-1.5 border-2 rounded-lg text-sm focus:outline-none focus:border-yellow-400"
                    style={{ borderColor: s.isLunch ? '#fcd34d' : '#e5e7eb' }}
                  />
                  <button
                    onClick={() => toggleLunch(i)}
                    title="Mark as lunch break"
                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 text-base transition-all"
                    style={s.isLunch
                      ? { borderColor: '#fbbf24', background: '#fef9c3' }
                      : { borderColor: '#e5e7eb', background: '#fff', opacity: 0.4 }}
                  >🍱</button>
                  <button
                    onClick={() => insertAfter(i)}
                    title="Insert new slot after this one"
                    className="w-8 h-8 flex items-center justify-center rounded-lg border-2 text-sm font-bold transition-all hover:scale-110"
                    style={{ borderColor: '#86efac', background: '#f0fdf4', color: '#16a34a' }}
                  >+</button>
                  <button
                    onClick={() => removeSlot(i)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 border-2 border-transparent transition-all text-lg leading-none"
                  >×</button>
                </div>
              ))}
          </div>

          <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
            <button
              onClick={applyConfig}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: '#2b2240' }}
            >Apply</button>
            <button
              onClick={() => setShowSettings(false)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 text-gray-500 hover:bg-gray-50"
            >Cancel</button>
          </div>
        </div>
      )}

      {!selectedMentorId ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-10 text-center text-gray-400 text-sm">
          Select a mentor above to view and edit their timetable.
        </div>
      ) : loading ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
            <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <span className="text-sm text-gray-400">Loading timetable…</span>
          </div>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="grid border-b border-gray-100 animate-pulse" style={{ gridTemplateColumns: '11rem repeat(6, 1fr) 7rem' }}>
              <div className="px-4 py-3"><div className="h-3 bg-gray-100 rounded w-20" /></div>
              {DAYS.map(d => <div key={d} className="px-2 py-3 flex justify-center"><div className="h-3 bg-gray-100 rounded w-16" /></div>)}
              <div className="px-2 py-3"><div className="h-6 bg-gray-100 rounded w-14 mx-auto" /></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {mentorStudents.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-700">
              No active students assigned to this mentor yet. Assign students from the Students page first.
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr style={{ background: '#312741' }}>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white w-44 sticky left-0" style={{ background: '#312741' }}>
                      Time Slot
                    </th>
                    {DAYS.map(day => (
                      <th key={day} className="px-2 py-3 text-center text-xs font-semibold text-white min-w-[120px]">
                        {day}
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center text-xs font-semibold w-28" style={{ color: '#ffe500' }}>
                      Add (Mon–Fri)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adminTimeSlots.map((slot, idx) => {
                    if (slot.isLunch) {
                      return (
                        <tr key={idx} style={{ background: '#fef9c3' }}>
                          <td colSpan={DAYS.length + 2} className="px-4 py-2 text-center text-xs font-bold text-amber-700">
                            🍱 Lunch Break
                          </td>
                        </tr>
                      );
                    }

                    const hasStudents = DAYS.some(d => (grid[d]?.[slot.start] ?? []).length > 0);
                    if (hideEmptyRows && !hasStudents) return null;

                    const isEditingThisRow = editingRow === slot.start;

                    return (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        {/* Time label */}
                        <td className="px-4 py-2 text-xs font-semibold whitespace-nowrap sticky left-0 bg-white"
                          style={{ color: '#312741', borderRight: '2px solid #e5e7eb' }}>
                          {slot.label}
                        </td>

                        {/* Day cells — multiple students per cell */}
                        {DAYS.map(day => {
                          const students = grid[day]?.[slot.start] ?? [];
                          return (
                            <td key={day} className="px-2 py-1.5 text-center align-top">
                              {students.length > 0 ? (
                                <div className="flex flex-col gap-1 items-center">
                                  {students.map(s => {
                                    // Only show move/remove controls in the first day this student appears
                                    const firstDay = DAYS.find(d => grid[d]?.[slot.start]?.some(x => x.studentId === s.studentId));
                                    const isFirstDay = firstDay === day;
                                    const isMoving = isFirstDay && movingStudent?.studentId === s.studentId && movingStudent?.fromSlot === slot.start;
                                    return (
                                      <div key={s.studentId} className="flex flex-col items-center gap-0.5">
                                        <div className="flex items-center gap-1">
                                          <span
                                            className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold cursor-pointer transition-transform hover:scale-105"
                                            style={{ background: '#ede9fe', color: '#5b21b6' }}
                                            onMouseEnter={e => hover.onEnter(s.studentId, s.studentName, e)}
                                            onMouseMove={hover.onMove}
                                            onMouseLeave={hover.onLeave}
                                          >
                                            {s.studentName}
                                          </span>
                                          {isFirstDay && (
                                            <>
                                              <button
                                                onClick={() => setMovingStudent(isMoving ? null : { studentId: s.studentId, fromSlot: slot.start })}
                                                disabled={saving}
                                                className="text-gray-300 hover:text-blue-500 transition-colors text-xs leading-none disabled:opacity-40"
                                                title={`Change time slot for ${s.studentName}`}
                                              >✎</button>
                                              <button
                                                onClick={() => handleRemoveStudent(slot.start, s.studentId, s.studentName)}
                                                disabled={saving}
                                                className="text-gray-300 hover:text-red-500 transition-colors text-base leading-none disabled:opacity-40"
                                                title={`Remove ${s.studentName} from all days`}
                                              >×</button>
                                            </>
                                          )}
                                        </div>
                                        {isMoving && (
                                          <select
                                            autoFocus
                                            value=""
                                            onChange={e => { if (e.target.value) handleMoveStudent(s.studentId, s.studentName, slot.start, e.target.value); }}
                                            onBlur={() => setMovingStudent(null)}
                                            className="w-36 text-xs border-2 border-blue-400 rounded-lg px-1.5 py-1 outline-none bg-white mt-1"
                                            disabled={saving}
                                          >
                                            <option value="">— move to… —</option>
                                            {adminTimeSlots.filter(ts => !ts.isLunch && ts.start !== slot.start).map(ts => (
                                              <option key={ts.start} value={ts.start}>{ts.label}</option>
                                            ))}
                                          </select>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="text-gray-200 text-xs">—</span>
                              )}
                            </td>
                          );
                        })}

                        {/* Add column — one button per row, applies Mon–Fri */}
                        <td className="px-2 py-1.5 text-center">
                          {isEditingThisRow ? (
                            <select
                              autoFocus
                              defaultValue=""
                              onChange={e => handleAssignWeekdays(slot.start, e.target.value)}
                              onBlur={() => setEditingRow(null)}
                              className="w-full text-xs border-2 border-yellow-400 rounded-lg px-1.5 py-1 outline-none bg-white"
                              disabled={saving}
                            >
                              <option value="">— pick student —</option>
                              {availableStudents.map(s => (
                                <option key={s.studentId} value={s.studentId}>{s.name}</option>
                              ))}
                              {availableStudents.length === 0 && (
                                <option disabled>All students assigned</option>
                              )}
                            </select>
                          ) : (
                            <button
                              onClick={() => setEditingRow(slot.start)}
                              disabled={saving || availableStudents.length === 0}
                              className="px-3 py-1 rounded-lg text-xs font-semibold border-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                              style={{ borderColor: '#ffe500', color: '#312741', background: '#fefce8' }}
                              title={availableStudents.length === 0 ? 'All students are already assigned to this slot' : 'Add student Mon–Fri'}
                            >
                              + Add
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Orphaned students panel — assigned to a slot that no longer exists */}
          {orphanedStudents.length > 0 && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <span className="text-sm font-semibold text-red-700">
                  {orphanedStudents.length} student{orphanedStudents.length > 1 ? 's' : ''} hidden — their old slot no longer exists after the time change
                </span>
              </div>
              <p className="text-xs text-red-600 mb-3">Remove and re-add them to a current slot using the ✎ icon or the + Add button.</p>
              <div className="flex flex-wrap gap-2">
                {orphanedStudents.map(s => (
                  <div key={s.studentId} className="flex items-center gap-2 bg-white border border-red-200 rounded-lg px-3 py-1.5">
                    <span className="text-xs font-semibold text-gray-700">{s.studentName}</span>
                    <span className="text-xs text-red-400 font-mono">was {fmt12(s.slotStart)}</span>
                    <button
                      onClick={() => handleRemoveStudent(s.slotStart, s.studentId, s.studentName)}
                      disabled={saving}
                      className="text-red-300 hover:text-red-600 text-base leading-none disabled:opacity-40 ml-1"
                      title={`Remove ${s.studentName} so they can be re-added to a new slot`}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unassigned students panel */}
          {mentorStudents.length > 0 && (
            <div className={`rounded-xl border p-4 ${
              unassignedStudents.length > 0
                ? 'border-orange-300 bg-orange-50'
                : 'border-green-200 bg-green-50'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {unassignedStudents.length > 0 ? (
                  <>
                    <svg className="w-4 h-4 text-orange-500 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                    <span className="text-sm font-semibold text-orange-700">
                      {unassignedStudents.length} student{unassignedStudents.length > 1 ? 's' : ''} not yet assigned to any slot
                    </span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span className="text-sm font-semibold text-green-700">All students are assigned to a slot</span>
                  </>
                )}
              </div>
              {unassignedStudents.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {unassignedStudents.map(s => (
                    <div key={s.studentId} className="flex items-center gap-2 bg-white border border-orange-200 rounded-lg px-3 py-1.5">
                      <span className="text-xs font-mono font-bold text-orange-500">{s.studentId}</span>
                      <span
                        className="text-xs font-semibold text-gray-700 cursor-pointer hover:text-purple-700 hover:underline transition-colors"
                        onMouseEnter={e => hover.onEnter(s.studentId, s.name, e)}
                        onMouseMove={hover.onMove}
                        onMouseLeave={hover.onLeave}
                      >{s.name}</span>
                      <span className="text-xs text-gray-400">{s.course}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400">
            Click <strong>+ Add</strong> to assign a student to <strong>Monday–Friday</strong> in that slot.
            Click <strong>✎</strong> to move a student to a different time slot.
            Click <strong>×</strong> to remove a student and free up their slot.
            Hover over a student name to see attendance &amp; task stats.
          </p>
        </>
      )}

      {/* Student hover card */}
      {hover.hovered && (
        <StudentHoverCard
          studentId={hover.hovered.studentId}
          studentName={hover.hovered.studentName}
          pos={hover.hovered.pos}
          onCardEnter={hover.onCardEnter}
          onCardLeave={hover.onCardLeave}
          onViewDetails={() => {
            const s = hover.hovered!;
            hover.dismiss();
            setDetailStudent({ studentId: s.studentId });
          }}
          onReassign={() => {
            const s = hover.hovered!;
            hover.dismiss();
            const mentor = mentors.find(m => m.mentorId === selectedMentorId);
            setReassignTarget({
              studentId: s.studentId,
              studentName: s.studentName,
              mentorId: selectedMentorId,
              mentorName: mentor?.mentorName ?? selectedMentorId,
            });
          }}
          onDeactivate={() => {
            const s = hover.hovered!;
            hover.dismiss();
            const slotStart = Object.entries(grid).flatMap(([, dayMap]) =>
              Object.entries(dayMap).flatMap(([start, students]) =>
                students.some(x => x.studentId === s.studentId) ? [start] : []
              )
            )[0];
            if (slotStart) handleRemoveStudent(slotStart, s.studentId, s.studentName);
          }}
        />
      )}

      {/* Reassign mentor modal */}
      {reassignTarget && (
        <ReassignMentorModal
          studentId={reassignTarget.studentId}
          studentName={reassignTarget.studentName}
          currentMentorId={reassignTarget.mentorId}
          currentMentorName={reassignTarget.mentorName}
          adminPassword={adminPassword}
          onClose={() => setReassignTarget(null)}
          onDone={(_newMentorId) => {
            setReassignTarget(null);
            // Remove from local state since student is no longer under this mentor
            setMentorStudents(prev => prev.filter(s => s.studentId !== reassignTarget.studentId));
            setSlots(prev => prev.filter(s => s.studentId !== reassignTarget.studentId));
            invalidateCache(selectedMentorId);
          }}
        />
      )}

      {/* Student detail modal */}
      {detailStudent && (
        <StudentDetailModal
          studentId={detailStudent.studentId}
          adminPassword={adminPassword}
          onClose={() => setDetailStudent(null)}
          onReassign={(t) => {
            setDetailStudent(null);
            setReassignTarget({
              studentId: t.studentId,
              studentName: t.studentName,
              mentorId: t.currentMentorId,
              mentorName: t.currentMentorName,
            });
          }}
          onDeactivated={(id) => {
            // Deactivating takes them off the timetable — drop them here too
            setMentorStudents(prev => prev.filter(s => s.studentId !== id));
            setSlots(prev => prev.filter(s => s.studentId !== id));
            invalidateCache(selectedMentorId);
          }}
        />
      )}
    </div>
  );
};

// ==================== SENIOR TIMETABLE VIEW ==================

const SeniorTimetableView: React.FC<{ mentorId: string }> = ({ mentorId }) => {
  const [juniors, setJuniors] = useState<JuniorMentor[]>([]);
  const [loadingJuniors, setLoadingJuniors] = useState(true);
  const [selectedMentorId, setSelectedMentorId] = useState(mentorId);

  useEffect(() => {
    setLoadingJuniors(true);
    getJuniorMentorOverview(mentorId)
      .then(res => setJuniors(res.juniors ?? []))
      .catch(err => toast.error('Failed to load junior mentors: ' + err.message))
      .finally(() => setLoadingJuniors(false));
  }, [mentorId]);

  const tabs = [
    { mentorId, label: 'My Timetable' },
    ...juniors.map(j => ({ mentorId: j.mentorId, label: j.mentorName })),
  ];

  return (
    <div className="space-y-4">
      {!loadingJuniors && tabs.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {tabs.map(t => (
            <button
              key={t.mentorId}
              onClick={() => setSelectedMentorId(t.mentorId)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all"
              style={selectedMentorId === t.mentorId
                ? { background: '#2b2240', color: '#F0B429', borderColor: '#2b2240' }
                : { borderColor: '#e5e7eb', color: '#6b7280', background: '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      {loadingJuniors ? (
        <div className="flex items-center justify-center h-48 text-gray-400">Loading team data…</div>
      ) : (
        <MentorTimetableView key={selectedMentorId} mentorId={selectedMentorId} editable={selectedMentorId === mentorId} />
      )}
    </div>
  );
};

// ==================== MAIN PAGE ==============================

const TimetablePage: React.FC = () => {
  const { session } = useTracker();
  const isAdmin = session?.kind === 'admin' || session?.kind === 'coordinator';
  const isSenior = session?.kind === 'mentor' && session.role === 'Senior';
  const mentorId = session?.kind === 'mentor' ? session.mentorId : '';
  const adminPassword = session?.kind === 'admin'
    ? session.adminPassword
    : session?.kind === 'coordinator'
    ? `COORD:${session.mentorId}`
    : '';
  const [sendingReminders, setSendingReminders] = React.useState(false);
  const [showReminderPicker, setShowReminderPicker] = React.useState(false);
  const [reminderMentors, setReminderMentors] = React.useState<MentorInfo[]>([]);
  const [loadingMentors, setLoadingMentors] = React.useState(false);

  const openReminderPicker = async () => {
    setShowReminderPicker(true);
    if (reminderMentors.length === 0) {
      setLoadingMentors(true);
      try {
        const res = await getAdminMentors(adminPassword);
        setReminderMentors((res.mentors as MentorInfo[]).filter((m: any) => m.status !== 'Inactive'));
      } catch (err: any) { toast.error(err.message); }
      finally { setLoadingMentors(false); }
    }
  };

  const handleSendReminders = async (pickedMentorId: string) => {
    setShowReminderPicker(false);
    setSendingReminders(true);
    try {
      const res = await sendClassReminders(adminPassword, undefined, pickedMentorId || undefined);
      if (res.message) { toast.info(res.message); }
      else { toast.success(`✅ Reminders sent: ${res.sent} emails${res.skipped ? ` (${res.skipped} skipped — no email)` : ''}`); }
    } catch (err: any) { toast.error(err.message); }
    finally { setSendingReminders(false); }
  };

  const todayKey = todayDayKey();
  return (
    <TrackerLayout>
      <div className="space-y-5 max-w-7xl mx-auto">

        {/* ── Premium header card ── */}
        <div className="rounded-2xl overflow-hidden shadow-sm"
          style={{ background: 'linear-gradient(135deg,#1e1730 0%,#2b2240 55%,#3d2f5e 100%)' }}>
          <div className="px-5 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">📅</span>
                <h1 className="text-xl font-black text-white">
                  {isAdmin ? 'Timetable' : 'My Schedule'}
                </h1>
                {todayKey && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full animate-pulse"
                    style={{ background: '#F0B429', color: '#2b2240' }}>
                    Today: {todayKey}
                  </span>
                )}
              </div>
              <p className="text-xs text-white/55">
                {isAdmin
                  ? 'Assign students to time slots — each assignment applies Mon–Fri automatically'
                  : isSenior
                  ? 'View your timetable or any junior mentor\'s schedule'
                  : 'Your weekly class schedule'}
              </p>
            </div>
            {isAdmin && (
              <button onClick={openReminderPicker} disabled={sendingReminders}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white border border-white/20 hover:bg-white/10 transition-all disabled:opacity-50 whitespace-nowrap"
                title="Send class reminder emails to a mentor's scheduled students">
                {sendingReminders ? '⏳ Sending…' : '📧 Send Reminders'}
              </button>
            )}
          </div>
          {/* Day pills */}
          <div className="px-5 pb-4 flex gap-2 flex-wrap">
            {DAYS.map(d => {
              const isToday = d === todayKey;
              return (
                <span key={d} className="px-3 py-1 rounded-full text-xs font-bold transition-all"
                  style={isToday
                    ? { background: '#F0B429', color: '#2b2240' }
                    : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
                  {d}
                </span>
              );
            })}
          </div>
        </div>

        {isAdmin
          ? <AdminTimetableView adminPassword={adminPassword} />
          : isSenior
          ? <SeniorTimetableView mentorId={mentorId} />
          : <MentorTimetableView mentorId={mentorId} />}
      </div>

      {/* Reminder mentor picker modal */}
      {showReminderPicker && (
        <div className="fixed inset-0 z-[300]">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowReminderPicker(false)} />
          <div className="absolute bg-white rounded-2xl shadow-2xl w-[calc(100%-2rem)] max-w-sm flex flex-col overflow-hidden"
            style={{
              animation: 'scPopIn .28s cubic-bezier(.34,1.56,.64,1) both',
              maxHeight: '80vh',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
            }}>
            <div className="px-5 py-4 flex-shrink-0" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
              <p className="text-white font-bold text-base">📧 Send Class Reminders</p>
              <p className="text-white/60 text-xs mt-0.5">Select a mentor — only their students will receive the email</p>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              {loadingMentors ? (
                <p className="text-center text-gray-400 py-4 text-sm">Loading mentors…</p>
              ) : (
                <>
                  {reminderMentors.map(m => (
                    <button key={m.mentorId}
                      onClick={() => handleSendReminders(m.mentorId)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-gray-100 hover:border-purple-300 hover:bg-purple-50 text-left transition-all group">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)', color: '#ffe500' }}>
                        {(m.mentorName || '').split(' ').map((n: string) => n[0] || '').join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-800 group-hover:text-purple-700">{m.mentorName}</p>
                        <p className="text-xs text-gray-400">{m.mentorId} · {(m as any).role || 'Standard'}</p>
                      </div>
                      <svg className="w-4 h-4 text-gray-300 group-hover:text-purple-500" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>
                  ))}
                </>
              )}
            </div>
            <div className="px-5 pb-4 flex-shrink-0">
              <button onClick={() => setShowReminderPicker(false)}
                className="w-full py-2.5 rounded-lg text-sm font-medium text-gray-600 border-2 border-gray-200 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </TrackerLayout>
  );
};

export default TimetablePage;
